import { supabase } from "@/lib/supabase/client";
import {
  isAccountBlockedResponse,
  signalAccountBlocked,
} from "@/lib/account-block";
import { notOnThisComputerMessage } from "@bash/shared";
import { apiUrl, responseSnippet } from "./base";

/**
 * A refused API call with its structured fields: the HTTP status and the body's `error` code, when it
 * has one. The message stays "<status> <first 200 characters of the body>", as before, except for a
 * feature this computer cannot reach yet (box hosts), whose message is its plain words.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function parseBody(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function errorCodeOf(body: string): string | null {
  const code = (parseBody(body) as { error?: unknown } | null)?.error;
  return typeof code === "string" ? code : null;
}

export async function getOptionalAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function getRequiredAccessToken(
  errorMessage = "Not authenticated"
): Promise<string> {
  const token = await getOptionalAccessToken();
  if (!token) throw new Error(errorMessage);
  return token;
}

export async function authedFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getRequiredAccessToken();
  const baseHeaders: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (init.body != null) {
    baseHeaders["Content-Type"] = "application/json";
  }
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: { ...baseHeaders, ...(init.headers ?? {}) },
    credentials: "include",
  });
  if (!res.ok) {
    // A blocked account (structured 403, read from a clone): ask the gate to
    // re-check. The caller still gets the untouched body in the error.
    if (await isAccountBlockedResponse(res)) signalAccountBlocked();
    const body = await res.text().catch(() => "");
    // Box hosts: a feature that cannot reach this computer yet says so in its own words.
    const plain = notOnThisComputerMessage(parseBody(body));
    throw new ApiError(
      plain ?? `${res.status} ${body.slice(0, 200)}`,
      res.status,
      errorCodeOf(body)
    );
  }
  return res;
}

/**
 * Like `authedFetch` but tolerates an anonymous caller. Use for public
 * endpoints that return richer state when a bearer token is present.
 */
export async function optionalAuthedFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getOptionalAccessToken();
  const baseHeaders: Record<string, string> = {};
  if (token) baseHeaders.Authorization = `Bearer ${token}`;
  if (init.body != null) baseHeaders["Content-Type"] = "application/json";
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: { ...baseHeaders, ...(init.headers ?? {}) },
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`${res.status} ${await responseSnippet(res)}`);
  }
  return res;
}
